import React, { useState, useEffect } from 'react';
import './HeroSection.css';

// Local asset URLs
const ASSETS = {
  posterDesktop: "/videos/poster-desktop.jpg",
  posterMobile: "/videos/poster-mobile.jpg",
  instagramIcon: "/icons8-instagram-500.png",
  whatsappIcon: "/icons8-whatsapp-500.png", 
  logoMoya: "/logo1.svg",
  fssaiLogo: "/Fssai.png",
  leafIcon: "/LEAF.svg", // Using button.svg as it contains the leaf design
  playIcon: "/PlayCircle.svg",
  isiMark: "/ISI.png"
};

const HeroSection = () => {
  // Mobile detection for video switching
  const [isMobile, setIsMobile] = useState(false);
  // Factory tour mode state
  const [isFactoryTourMode, setIsFactoryTourMode] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      console.log('Screen width:', window.innerWidth, 'Mobile:', mobile);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Factory tour handlers
  const handleFactoryTourClick = () => {
    setIsFactoryTourMode(true);
    // Autoplay video after state update and skip to 2 seconds
    setTimeout(() => {
      const video = document.querySelector('.background-video');
      if (video) {
        // Set start time to 2 seconds for factory tour
        video.currentTime = 2;
        video.play().catch(e => console.log('Video play error:', e));
      }
    }, 100);
  };

  const handleCloseFactoryTour = () => {
    setIsFactoryTourMode(false);
  };

  return (
    <div className="hero-container">
      {/* Video Background */}
      <div className="video-background">
        <video
          key={isFactoryTourMode ? 'factory-tour' : (isMobile ? 'mobile' : 'desktop')}
          autoPlay
          muted
          loop={!isFactoryTourMode}
          playsInline
          preload="auto"
          className="background-video"
          poster={isMobile ? ASSETS.posterMobile : ASSETS.posterDesktop}
          onPause={(e) => {
            if (!isFactoryTourMode) {
              e.target.play().catch(() => {});
            }
          }}
          onEnded={(e) => {
            if (!isFactoryTourMode) {
              e.target.currentTime = 0;
              e.target.play().catch(() => {});
            }
          }}
        >
          {isFactoryTourMode ? (
            <source src="https://d2dd5aqsv5ojgh.cloudfront.net/videos/factory.mp4#t=2" type="video/mp4" />
          ) : isMobile ? (
            <>
              <source src="https://d2dd5aqsv5ojgh.cloudfront.net/videos/moya-mobile.mp4" type="video/mp4" />
            </>
          ) : (
            <source src="https://d2dd5aqsv5ojgh.cloudfront.net/videos/Desktop_VidSC.mp4" type="video/mp4" />
          )}
          {/* Fallback image */}
          <img src={isMobile ? ASSETS.posterMobile : ASSETS.posterDesktop} alt={isFactoryTourMode ? "Factory Tour" : "Moya Lite Natural Water"} />
        </video>
      </div>

      {/* Main Content Overlay */}
      <div className="content-overlay">
        {/* Logo Container / Factory Tour Title */}
        <div className="logo-container">
          {isFactoryTourMode ? (
            <div className="factory-tour-title">
              <h2>Factory<br />Tour</h2>
            </div>
          ) : (
            <img src={ASSETS.logoMoya} alt="Moya Lite" className="main-logo" />
          )}
        </div>

        {/* Main Headline - Hidden in Factory Tour Mode */}
        {!isFactoryTourMode && (
          <div className="main-headline">
            <h1>
              As Refreshing As<br />A Mountain Stream
            </h1>
          </div>
        )}

        {/* CTA Button - Hidden in Factory Tour Mode */}
        {!isFactoryTourMode && (
          <a href="https://wa.me/917799799632?text=Hi%2C%20I%27d%20like%20to%20inquire%20about%20Moya%20Lite%20water" className="cta-button">
            <span className="cta-text">Inquire Now</span>
            <img src={ASSETS.leafIcon} alt="" className="cta-leaf" />
          </a>
        )}

        {/* Desktop Factory Tour Button - Top Right */}
        <div className="factory-tour-container">
          {isFactoryTourMode ? (
            <button className="factory-tour-button" onClick={handleCloseFactoryTour}>
              <span>Close Tour</span>
              <span className="close-icon">×</span>
            </button>
          ) : (
            <button className="factory-tour-button" onClick={handleFactoryTourClick}>
              <span>Factory Tour</span>
              <img src={ASSETS.playIcon} alt="Play" className="play-icon" />
            </button>
          )}
        </div>

        {/* Mobile Center Play - "Watch how it's made" */}
        {isMobile && !isFactoryTourMode && (
          <button className="mobile-center-play" onClick={handleFactoryTourClick}>
            <div className="mobile-center-play-circle">
              <svg viewBox="0 0 24 24" fill="none" className="mobile-center-play-icon">
                <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11.04-6.86a1 1 0 0 0 0-1.72L9.5 4.28a1 1 0 0 0-1.5.86Z" fill="#ffffff" />
              </svg>
            </div>
            <span className="mobile-center-play-text">Watch how it's made</span>
          </button>
        )}

        {/* Mobile Close Button for Factory Tour */}
        {isMobile && isFactoryTourMode && (
          <button className="mobile-close-button" onClick={handleCloseFactoryTour}>
            <span className="mobile-close-icon">×</span>
          </button>
        )}

        {/* Social Media Icons */}
        <div className="social-media">
          <a href="https://instagram.com/moyalite" target="_blank" rel="noopener noreferrer" className="social-icon">
            <img src={ASSETS.instagramIcon} alt="Instagram" />
          </a>
          <a href="https://wa.me/917799799632?text=Hi%2C%20I%27d%20like%20to%20inquire%20about%20Moya%20Lite%20water" target="_blank" rel="noopener noreferrer" className="social-icon">
            <img src={ASSETS.whatsappIcon} alt="WhatsApp" />
          </a>
        </div>

        {/* Certification Logos */}
        <div className="certifications">
          <div className="cert-background">
            <img src={ASSETS.fssaiLogo} alt="FSSAI Certified" className="fssai-logo" />
            <img src={ASSETS.isiMark} alt="ISI Mark" className="isi-logo" />
          </div>
        </div>

     
      </div>
    </div>
  );
};

export default HeroSection; 
